import type { CustomSkillRepository } from '../persistence/customSkillRepository.ts';
import type { CustomBackgroundRepository } from '../persistence/customBackgroundRepository.ts';
import type { CustomItemRepository } from '../persistence/customItemRepository.ts';
import type { LibraryStatus } from '../persistence/customContentRepository.ts';
import { resetCustomContent, setCustomContent, setSharedContent } from '../content/dccCoreContent.ts';
import type { AttackProfile, Item, Skill } from '../content/types.ts';

/**
 * Loading every custom-content library, ONCE, deterministically.
 *
 * Each library used to be loaded by its own service, and each called
 * `setCustomContent` and reported the rejections it saw at that moment. With
 * the three loads running concurrently, whichever finished first composed a
 * catalogue that did not yet contain the others - so a Background depending on
 * a custom Skill or Item could report "not loaded, dependency missing", the
 * dependency could arrive a moment later, the catalogue could recompose
 * correctly, and the stale warning would still be on screen. The final
 * catalogue was right and the message was wrong, depending on disk timing.
 *
 * The fix is to separate READING from COMPOSING. All three libraries are read
 * first, no intermediate composition is treated as final, and the catalogue is
 * composed exactly once from all three together. The rejections reported are
 * the ones from that single composition, so the message always describes the
 * catalogue the player actually has.
 *
 * The unreadable-file protections are untouched: each repository still refuses
 * to be written over, and a library that cannot be read still contributes an
 * empty placeholder and its own problem message.
 */

export interface CustomContentDeps {
  customSkills: CustomSkillRepository;
  customBackgrounds: CustomBackgroundRepository;
  customItems: CustomItemRepository;
}

export interface BootstrapResult {
  problems: string[];
  statuses: {
    skills: LibraryStatus;
    backgrounds: LibraryStatus;
    items: LibraryStatus;
  };
}

/**
 * Item definitions that belong to the TABLE rather than to this player: Items a
 * GM awarded, loot, things another crawler put in the Party Inventory. The host
 * fetches and validates them; they are composed alongside the player's own
 * Items so that an Item a crawler holds means the same thing on every sheet.
 *
 * They are composed, never SAVED: nothing here writes them into the player's
 * own library, which stays exactly what the player authored. They live in their
 * own slot (`setSharedItems`), so saving a custom Item cannot drop them.
 */
export interface SharedContent {
  items?: readonly Item[];
  /** The Skill and Attack halves of GM-made weapons among those Items. */
  skills?: readonly Skill[];
  attacks?: readonly AttackProfile[];
}

export async function loadAllCustomContent(deps: CustomContentDeps, shared: SharedContent = {}): Promise<BootstrapResult> {
  // Reading concurrently is fine and desirable; it is COMPOSING concurrently
  // that produced stale reports.
  const [items, skills, backgrounds] = await Promise.all([
    deps.customItems.load(),
    deps.customSkills.load(),
    deps.customBackgrounds.load(),
  ]);

  // Cleared first so a second bootstrap - a test, or a reload - starts from the
  // printed content rather than layering onto whatever was there.
  resetCustomContent();

  // Shared (table) Items go into their own slot, which the player's library can
  // never overwrite. Installed before the player's content so the single
  // composition reported below is the finished catalogue.
  setSharedContent({ items: shared.items ?? [], skills: shared.skills ?? [], attacks: shared.attacks ?? [] });

  // ONE composition, from all three. Its rejections are the only ones reported,
  // because they are the only ones describing the finished catalogue.
  const rejected = setCustomContent({
    items: items.library.items,
    skills: skills.library.skills,
    attacks: skills.library.attacks,
    backgrounds: backgrounds.library.backgrounds,
  });

  const problems: string[] = [
    ...items.problems,
    ...skills.problems,
    ...backgrounds.problems,
  ];

  for (const { item, reason } of rejected.items) {
    problems.push(`Custom Item "${item.name}" was not loaded because ${reason}.`);
  }
  for (const { item, reason } of rejected.skills) {
    problems.push(`Custom Skill "${item.name}" was not loaded because ${reason}.`);
  }
  for (const { item, reason } of rejected.backgrounds) {
    problems.push(`Custom Background "${item.name}" was not loaded because ${reason}.`);
  }

  if (items.status === 'unreadable') {
    problems.push(
      'Your custom Item file has been left exactly as it is. Crawlers keep the Items they own; ' +
        'those Items will not be usable until the file can be read.',
    );
  }
  if (skills.status === 'unreadable') {
    problems.push(
      'Your custom Skill file has been left exactly as it is. Crawlers keep the Skills and ' +
        'Ranks they had; those Skills will not appear until the file can be read.',
    );
  }
  if (backgrounds.status === 'unreadable') {
    problems.push(
      'Your custom Background file has been left exactly as it is. Crawlers keep the Skills ' +
        'their Backgrounds granted; the Backgrounds will not be offered until the file can be read.',
    );
  }

  return {
    problems,
    statuses: {
      skills: skills.status,
      backgrounds: backgrounds.status,
      items: items.status,
    },
  };
}
